import { Button, Dialog, Text, XStack, YStack } from '@my/ui'
import { Check as CheckIcon, ChevronDown } from '@tamagui/lucide-icons'
import { useActiveProjects } from 'app/data/projects/useProjects'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface TaskProjectPickerProps {
  projectId: string | null
  onChange: (projectId: string) => void
}

/**
 * TaskProjectPicker component allows users to select a project for a task.
 */
export function TaskProjectPicker({ projectId, onChange }: TaskProjectPickerProps) {
  const { data: projects } = useActiveProjects()
  const [isOpen, setIsOpen] = useState(false)

  // Find the "Unassigned" project
  const generalProject = useMemo(() => {
    return projects?.find(project => project.name === "Unassigned")
  }, [projects])

  // Set the current project, defaulting to "Unassigned" if no projectId is provided
  const currentProject = useMemo(() => {
    console.log('Current project calculation:', {
      projectId,
      generalProjectId: generalProject?.id,
      projects: projects?.map((p) => ({ id: p.id, name: p.name })),
    })

    if (projectId) {
      return projects?.find((project) => project.id === projectId)
    }
    return generalProject
  }, [projects, projectId, generalProject])

  // Effect to set the default project to "Unassigned" if no projectId is provided
  useEffect(() => {
    console.log('TaskProjectPicker Effect:', {
      currentProjectId: projectId,
      generalProjectId: generalProject?.id,
      generalProjectName: generalProject?.name,
    })

    if (!projectId && generalProject) {
      console.log('Setting default project:', generalProject.id)
      onChange(generalProject.id)
    }
  }, [projectId, generalProject, onChange])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
  }, [])

  const handleProjectSelect = useCallback(
    (selectedProjectId: string) => {
      console.log('Project selected:', selectedProjectId)
      onChange(selectedProjectId)
      setIsOpen(false)
    },
    [onChange]
  )

  return (
    <>
      <Button unstyled alignSelf="flex-start" onPress={() => setIsOpen(true)}>
        <XStack
          borderWidth={2.5}
          borderColor={(currentProject?.color && currentProject.name !== 'Unassigned' ? currentProject.color : '$color5') as any}
          backgroundColor={(currentProject?.color && currentProject.name !== 'Unassigned' ? currentProject.color : '$color1') as any}
          paddingVertical="$1.5"
          paddingHorizontal="$2.5"
          borderRadius="$2"
          gap="$2"
          alignItems="center"
          alignSelf="flex-start"
          flexShrink={1}
          flexGrow={0}
          maxWidth="100%"
          cursor="pointer"
          hoverStyle={{ opacity: 0.9 }}
        >
          <Text
            fontSize="$3"
            fontWeight="500"
            color={(currentProject?.name === 'Unassigned' ? "$color6" : '$color1') as any}
          >
            {currentProject?.name !== 'Unassigned' ? currentProject?.name : 'Select Project'}
          </Text>
          <ChevronDown size={16} color={(currentProject?.name === 'Unassigned' ? "$color6" : '$color1') as any} />
        </XStack>
      </Button>

      {isOpen && (
        <Dialog modal open={isOpen} onOpenChange={handleOpenChange}>
          <Dialog.Portal>
            <Dialog.Overlay key="projectPickerOverlay" />
            <Dialog.Content
              key="projectPickerContent"
              shadowColor="$color10"
              shadowRadius={10}
              shadowOffset={{ width: 0, height: 0 }}
              shadowOpacity={0.2}
              minWidth={300}
              maxWidth={600}
            >
              <YStack padding="$4" gap="$4">
                <Text fontSize="$6" fontWeight="bold" letterSpacing={-0.5}>
                  Projects
                </Text>

                {/* Projects List */}
                <YStack gap="$2">
                  {projects?.map((project) => (
                    <Button
                      key={project.id}
                      unstyled
                      onPress={() => handleProjectSelect(project.id)}
                      cursor="pointer"
                    >
                      <XStack
                        justifyContent="space-between"
                        alignItems="center"
                        paddingVertical="$3"
                        paddingHorizontal="$3"
                        borderRadius="$3"
                        borderWidth={2}
                        borderColor={(project.color && project.name !== 'Unassigned' ? project.color : '$color6') as any}
                        backgroundColor={project.id === projectId ? (project.color && project.name !== 'Unassigned' ? project.color : '$color6') as any : 'transparent'}
                        hoverStyle={project.id === projectId ? undefined : { opacity: 0.7 }}
                        animation="100ms"
                      >
                        <Text
                          flex={1}
                          fontSize="$4"
                          letterSpacing={-0.5}
                          color={
                            project.id === projectId
                              ? '$color1'
                              : ((project.color || '$accentColor') as any)
                          }
                          fontWeight="bold"
                          opacity={1}
                        >
                          {project.name !== 'Unassigned' ? project.name : 'None'}
                        </Text>
                        {project.id === projectId && (
                          <XStack paddingLeft="$3">
                            <CheckIcon color="$color1" size={18} strokeWidth={3} />
                          </XStack>
                        )}
                      </XStack>
                    </Button>
                  ))}
                </YStack>
              </YStack>
            </Dialog.Content>
          </Dialog.Portal>
          <Dialog.Adapt when="sm">
            <Dialog.Sheet animation="200ms" modal dismissOnSnapToBottom snapPointsMode="fit">
              <Dialog.Sheet.Frame padding="$4" gap="$4">
                <Dialog.Adapt.Contents />
              </Dialog.Sheet.Frame>
              <Dialog.Sheet.Overlay
                animation="quick"
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
              />
            </Dialog.Sheet>
          </Dialog.Adapt>

          <Dialog.Adapt platform="native">
            <Dialog.Sheet animation="200ms" modal dismissOnSnapToBottom snapPointsMode="fit">
              <Dialog.Sheet.Frame padding="$4" gap="$4">
                <Dialog.Adapt.Contents />
              </Dialog.Sheet.Frame>
              <Dialog.Sheet.Overlay
                animation="quick"
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
              />
            </Dialog.Sheet>
          </Dialog.Adapt>
        </Dialog>
      )}
    </>
  )
}